/**
 * The message box (1.7.6, D-1.7.6-1): English and Spanish, each shown for editing; it opens with a draft the app
 * wrote from the fields; "Clean it up" shows what it would change before the office accepts; Undo keeps the previous
 * text. Nothing here sends anything — it is text in a box.
 *
 * The app never waits on the model: the boxes are usable the moment they show, and a draft that arrives goes only
 * into a box that is still empty and untouched. "Write a draft" over words the person typed asks first. Every call
 * that fails is said in words; nothing is thrown at the page.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LINK_PLACEHOLDER, diffChanges, wordDiff, type AnnounceLang, type DraftFields, type DraftResult, type DraftText } from '@shared/announcements';
import { useAnnounce } from '@/components/Announcer';
import { Button } from '@/components/ui/button';
import { errorText } from '@/features/people/api';
import { requestCleanup, requestDraft } from './api';

export interface DraftBoxProps {
  value: { en: string; es: string };
  onChange: (next: { en: string; es: string }) => void;
  /** What the draft is written from; null = nothing to draft from yet (no title). */
  request: { eventId?: string | null; fields?: Partial<DraftFields> } | null;
  /** Ask for a draft once when it opens — only into a box that is still empty and untouched. */
  autoDraft?: boolean;
  /** Each draft that arrives (the sheet fills an empty subject from it). */
  onDrafted?: (r: DraftResult) => void;
  disabled?: boolean;
  /** The help line about the link placeholder, when it is not the announcement's (an invitation always carries its buttons). */
  linkHelp?: string;
  /**
   * D-1028-ONELANG: how many of the people this reaches have Spanish on their own record, and of how many. English is
   * what the office sees; the Spanish version waits behind "Show Spanish version". null = not known here.
   */
  spanish?: { count: number; total: number } | null;
  /** Fields that belong with the Spanish version (the Spanish subject), shown with it. */
  spanishExtra?: ReactNode;
}

const LANGS: readonly AnnounceLang[] = ['en', 'es'];
type Two<T> = Record<AnnounceLang, T>;
const two = <T,>(v: T): Two<T> => ({ en: v, es: v });
/** When the fields change after the box opened, the first draft waits until they have been still this long. */
const SETTLE_MS = 700;

/**
 * Test ids: `draft-box-en`, `draft-box-es` (the two textareas), `draft-write` (Write a draft again),
 * `draft-working` (while the draft is being written — the boxes stay usable), `draft-source-en` / `draft-source-es`
 * (who wrote it, or why it is the plain wording), `draft-clean-en` / `draft-clean-es` (Clean it up),
 * `draft-diff` (what changed: the coloured text), `draft-diff-changes` (the same in words, a list),
 * `draft-accept`, `draft-keep` (accept the clean-up, or keep mine), `draft-undo-en` / `draft-undo-es`.
 * Also: `draft-replace-yes` / `draft-replace-no` (the second press before typed words are replaced),
 * `draft-failed` (the draft could not be written), `draft-note-en` / `draft-note-es` (what Clean it up answered).
 */
export function DraftBox({ value, onChange, request, autoDraft = false, onDrafted, disabled = false, linkHelp, spanish = null, spanishExtra }: DraftBoxProps) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const id = useId();
  /** The boxes as they are now — an answer that arrives later reads this, never the text of the render that asked. */
  const now = useRef(value);
  /** The person typed in the box (even if they emptied it again). */
  const touched = useRef<Two<boolean>>(two(false));
  /** The text a draft put in the box, to tell it from the person's own words. */
  const put = useRef<Two<string | null>>(two<string | null>(null));
  const alive = useRef(true);
  const asked = useRef(false);
  const first = useRef(true);
  const calls = useRef(0);
  const boxes = useRef<Two<HTMLTextAreaElement | null>>(two<HTMLTextAreaElement | null>(null));
  const writeButton = useRef<HTMLButtonElement | null>(null);
  const yesButton = useRef<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);

  const [showEs, setShowEs] = useState(false);
  const [working, setWorking] = useState(false);
  const [failed, setFailed] = useState(false);
  const [asking, setAsking] = useState(false);
  const [source, setSource] = useState<Two<Pick<DraftText, 'source' | 'why'> | null>>(two<Pick<DraftText, 'source' | 'why'> | null>(null));
  const [undo, setUndo] = useState<Two<string | null>>(two<string | null>(null));
  const [note, setNote] = useState<Two<{ text: string; bad: boolean } | null>>(two<{ text: string; bad: boolean } | null>(null));
  const [cleaning, setCleaning] = useState<AnnounceLang | null>(null);
  const [clean, setClean] = useState<{ lang: AnnounceLang; before: string; after: string } | null>(null);

  useEffect(() => {
    now.current = value;
  }, [value]);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const set = (next: Two<string>) => {
    now.current = next;
    onChange(next);
  };

  /** Ask for a draft. `replace`: the person pressed and agreed — both boxes take it; otherwise only an empty, untouched box. */
  const write = async (replace: boolean, quiet = false) => {
    if (!request) return;
    const mine = ++calls.current;
    setAsking(false);
    setFailed(false);
    setWorking(true);
    try {
      const r = await requestDraft(request);
      if (!alive.current || mine !== calls.current) return;
      onDrafted?.(r);
      const cur = now.current;
      const next = { ...cur };
      const back: Partial<Two<string | null>> = {};
      const from: Partial<Two<Pick<DraftText, 'source' | 'why'>>> = {};
      for (const l of LANGS) {
        const body = r[l]?.body ?? '';
        if (!body.trim()) continue;
        const free = !cur[l].trim() && !touched.current[l];
        if (!replace && !free) continue;
        back[l] = cur[l].trim() && cur[l] !== body ? cur[l] : null;
        next[l] = body;
        put.current[l] = body;
        touched.current[l] = false;
        from[l] = { source: r[l].source, why: r[l].why };
      }
      const filled = Object.keys(from) as AnnounceLang[];
      if (filled.length) {
        set(next);
        setSource((s) => ({ ...s, ...from }));
        setUndo((u) => ({ ...u, ...back }));
        setNote((n) => ({ en: filled.includes('en') ? null : n.en, es: filled.includes('es') ? null : n.es }));
        setClean((c) => (c && filled.includes(c.lang) ? null : c));
      }
      announce(filled.length ? t('draft176.drafted') : t('draft176.draftedKept'));
    } catch {
      if (!alive.current || mine !== calls.current) return;
      setFailed(true);
      if (!quiet) announce(t('draft176.failed'), 'assertive');
    } finally {
      if (alive.current && mine === calls.current) setWorking(false);
    }
  };

  // The first draft, once: when there is something to write from and both boxes are empty and untouched. Fields
  // that are still being typed (the request changed after the box opened) are given a moment to settle.
  const requestKey = request ? JSON.stringify(request) : '';
  useEffect(() => {
    const opening = first.current;
    first.current = false;
    if (!autoDraft || !requestKey || asked.current) return;
    const go = () => {
      if (asked.current) return;
      const cur = now.current;
      if (LANGS.some((l) => cur[l].trim() || touched.current[l])) return;
      asked.current = true;
      void write(false, true);
    };
    if (opening) {
      go();
      return;
    }
    const h = window.setTimeout(go, SETTLE_MS);
    return () => window.clearTimeout(h);
    // `write` reads the request of this render; the effect runs again only when what is asked for changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDraft, requestKey]);

  useEffect(() => {
    if (asking) yesButton.current?.focus();
  }, [asking]);
  useEffect(() => {
    if (clean) panel.current?.focus();
  }, [clean]);

  const onWrite = () => {
    // words that are the person's own (not a draft this box put there) are never replaced without a second press
    const typed = LANGS.some((l) => now.current[l].trim() && now.current[l] !== put.current[l]);
    if (typed) {
      setAsking(true);
      announce(t('draft176.replaceAsk'));
    } else void write(true);
  };

  const onType = (l: AnnounceLang, text: string) => {
    touched.current[l] = true;
    if (note[l]) setNote((n) => ({ ...n, [l]: null }));
    set({ ...now.current, [l]: text });
  };

  const onUndo = (l: AnnounceLang) => {
    const previous = undo[l];
    if (previous === null) return;
    touched.current[l] = true;
    put.current[l] = null;
    set({ ...now.current, [l]: previous });
    setUndo((u) => ({ ...u, [l]: null }));
    setSource((s) => ({ ...s, [l]: null }));
    announce(t('draft176.undone'));
    boxes.current[l]?.focus();
  };

  const onClean = async (l: AnnounceLang) => {
    const text = now.current[l];
    if (!text.trim()) return;
    setCleaning(l);
    setNote((n) => ({ ...n, [l]: null }));
    try {
      const r = await requestCleanup(text, l);
      if (!alive.current) return;
      if (r.refused) {
        setNote((n) => ({ ...n, [l]: { text: r.refused!, bad: true } }));
        announce(r.refused);
      } else if (!r.changed || r.text === text) {
        setNote((n) => ({ ...n, [l]: { text: t('draft176.nothing'), bad: false } }));
        announce(t('draft176.nothing'));
      } else {
        setClean({ lang: l, before: text, after: r.text });
        announce(t('draft176.changes', { count: changesOf(text, r.text).length }));
      }
    } catch (err) {
      if (!alive.current) return;
      const m = errorText(err);
      setNote((n) => ({ ...n, [l]: { text: m, bad: true } }));
      announce(m, 'assertive');
    } finally {
      if (alive.current) setCleaning(null);
    }
  };

  /** The changes in words: "teh" became "the" · added "," · removed "very" · spacing tidied (said once). */
  function changesOf(before: string, after: string): string[] {
    const out: string[] = [];
    for (const c of diffChanges(wordDiff(before, after))) {
      const from = c.from.trim();
      const to = c.to.trim();
      const words = c.spacing || (!from && !to) ? t('draft176.spacing') : from && to ? t('draft176.became', { from, to }) : to ? t('draft176.addedWord', { to }) : t('draft176.removedWord', { from });
      if (!out.includes(words)) out.push(words);
    }
    return out;
  }

  const onAccept = () => {
    if (!clean) return;
    const l = clean.lang;
    touched.current[l] = true;
    set({ ...now.current, [l]: clean.after });
    setUndo((u) => ({ ...u, [l]: clean.before }));
    setClean(null);
    announce(t('draft176.accepted'));
    boxes.current[l]?.focus();
  };
  const onKeep = () => {
    if (!clean) return;
    const l = clean.lang;
    setClean(null);
    announce(t('draft176.kept'));
    boxes.current[l]?.focus();
  };

  const helpId = `${id}-help`;
  // D-1028-ONELANG-8: when the number of Spanish readers is known, the Spanish box shows only if there are some (labelled
  // "Spanish wording (for N people)"); when it is not known here, the Spanish version waits behind its button
  const known = spanish !== null && spanish !== undefined;
  const esShown = known ? spanish.count > 0 : showEs;
  return (
    <div className="draft176" data-testid="draft-box">
      <div className="chips draft176-top">
        <Button ref={writeButton} variant="secondary" disabled={disabled || !request || working || asking} onClick={onWrite} data-testid="draft-write">
          {t('draft176.write')}
        </Button>
        {asking ? (
          <span className="draft176-ask" role="group" aria-labelledby={`${id}-ask`}>
            <b id={`${id}-ask`}>{t('draft176.replaceAsk')}</b>
            <Button ref={yesButton} disabled={disabled} onClick={() => write(true)} data-testid="draft-replace-yes">
              {t('draft176.replaceYes')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setAsking(false);
                writeButton.current?.focus();
              }}
              data-testid="draft-replace-no"
            >
              {t('draft176.notNow')}
            </Button>
          </span>
        ) : null}
      </div>
      {working ? (
        <p className="help" role="status" data-testid="draft-working">
          {t('draft176.working')}
        </p>
      ) : null}
      {failed && !working ? (
        <p className="field-err" data-testid="draft-failed">
          {t('draft176.failed')}
        </p>
      ) : null}

      <div className="draft176-onelang" data-testid="draft-onelang">
        {spanish && spanish.count > 0 ? (
          <p className="help" role="status" data-testid="draft-spanish-line">
            {value.es.trim() ? t('oneLang.line', { count: spanish.count, total: spanish.total }) : t('oneLang.lineNoText', { count: spanish.count, total: spanish.total })}
          </p>
        ) : null}
        {known ? null : (
          <Button variant="secondary" size="sm" aria-expanded={showEs} aria-controls={`${id}-es-panel`} onClick={() => setShowEs((x) => !x)} data-testid="draft-show-es">
            {showEs ? t('oneLang.hide') : t('oneLang.show')}
          </Button>
        )}
      </div>
      <div className="draft176-boxes">
        {LANGS.filter((l) => l === 'en' || esShown).map((l) => {
          const boxId = `${id}-${l}`;
          const src = source[l];
          const reviewing = clean?.lang === l;
          const parts = reviewing ? wordDiff(clean.before, clean.after) : [];
          return (
            <div className="field draft176-lang" key={l} id={l === 'es' ? `${id}-es-panel` : undefined}>
              {l === 'es' ? spanishExtra : null}
              <label htmlFor={boxId}>{l === 'es' && known && spanish.count > 0 ? t('oneLang.boxLabel', { count: spanish.count }) : t(`draft176.label.${l}`)}</label>
              <textarea
                id={boxId}
                ref={(node) => {
                  boxes.current[l] = node;
                }}
                className="input draft176-text"
                rows={9}
                lang={l}
                value={value[l]}
                disabled={disabled}
                readOnly={reviewing || cleaning === l}
                aria-describedby={`${src ? `${boxId}-src ` : ''}${helpId}`}
                onChange={(e) => onType(l, e.target.value)}
                data-testid={`draft-box-${l}`}
              />
              {src ? (
                <p className="help" id={`${boxId}-src`} data-testid={`draft-source-${l}`}>
                  {src.source === 'assistant' ? t('draft176.byAssistant') : (src.why ?? '')}
                </p>
              ) : null}
              <div className="chips">
                <Button variant="secondary" size="sm" className="draft176-btn" disabled={disabled || !value[l].trim() || cleaning !== null || clean !== null} aria-label={t('draft176.cleanFor', { lang: t(`draft176.lang.${l}`) })} onClick={() => onClean(l)} data-testid={`draft-clean-${l}`}>
                  {t('draft176.clean')}
                </Button>
                {undo[l] !== null && !reviewing ? (
                  <Button variant="secondary" size="sm" className="draft176-btn" disabled={disabled} aria-label={t('draft176.undoFor', { lang: t(`draft176.lang.${l}`) })} onClick={() => onUndo(l)} data-testid={`draft-undo-${l}`}>
                    {t('draft176.undo')}
                  </Button>
                ) : null}
              </div>
              {cleaning === l ? (
                <p className="help" role="status">
                  {t('draft176.cleaning')}
                </p>
              ) : null}
              {note[l] ? (
                <p className={note[l].bad ? 'field-err' : 'help'} data-testid={`draft-note-${l}`}>
                  {note[l].text}
                </p>
              ) : null}
              {reviewing ? (
                <div className="draft176-diff" role="group" aria-labelledby={`${boxId}-diff`} tabIndex={-1} ref={panel}>
                  <b id={`${boxId}-diff`}>{t('draft176.diffTitle', { lang: t(`draft176.lang.${l}`) })}</b>
                  <p className="help">{t('draft176.diffHelp')}</p>
                  <p className="draft176-marks" lang={l} data-testid="draft-diff">
                    {parts.map((p, n) =>
                      p.kind === 'same' ? (
                        <span key={n}>{p.text}</span>
                      ) : p.kind === 'del' ? (
                        <del key={n}>
                          <span className="sr-only">{t('draft176.removed')}: </span>
                          {p.text}
                        </del>
                      ) : (
                        <ins key={n}>
                          <span className="sr-only">{t('draft176.added')}: </span>
                          {p.text}
                        </ins>
                      ),
                    )}
                  </p>
                  <ul className="draft176-changes" aria-label={t('draft176.changesList')} data-testid="draft-diff-changes">
                    {changesOf(clean.before, clean.after).map((words) => (
                      <li key={words}>{words}</li>
                    ))}
                  </ul>
                  <div className="chips">
                    <Button disabled={disabled} onClick={onAccept} data-testid="draft-accept">
                      {t('draft176.accept')}
                    </Button>
                    <Button variant="secondary" onClick={onKeep} data-testid="draft-keep">
                      {t('draft176.keep')}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <p className="help" id={helpId}>
        {linkHelp ?? t('draft176.linkHelp', { link: LINK_PLACEHOLDER })}
      </p>
    </div>
  );
}
